import type { ReactNode } from 'react'
import { api } from '@/runtime'
import type {
  PhysicalDrive,
  StorageFilesystem,
  StorageInventory as Inventory,
  StorageMachine,
  StorageSection,
  StorageShare,
  StorageVolume,
} from '@/db/api'
import { statusDot, statusText, useBar } from '@/lib/colour'
import { fmtBytes, pctOf, shareOf } from '@/lib/storage'
import type { Level } from '@/lib/hosts'
import {
  backupWords,
  clientsWords,
  driveFacts,
  driveLevel,
  driveTitle,
  fsLevel,
  fsTypeWords,
  fsWords,
  machineLine,
  machinesByEstate,
  offWords,
  shareFacts,
  sortShares,
  volumeLevel,
  volumeLine,
  volumePct,
  volumeSize,
  volumesByWhere,
} from '@/lib/storageInventory'
import { Skeleton } from './Skeleton'
import { BackupRow, HostRow } from './Storage'
import { Failed, SectionTitle, Tag, cx } from './ui'

/**
 * Storage, the infra pack's third page (/infra/storage, 2026-10-31): every
 * drive, filesystem and share by machine, every Kubernetes volume by where it
 * lives, the cloud stores and the backups, from GET /api/storage/inventory
 * (internal/storage Inventory). Online only. Each section has its own state:
 * one whose source is off says why in a calm line (the volumes before the
 * ClusterRole grants them), one that failed is Failed with Try again, and the
 * rest stand. The first read is Skeletons; a later failure keeps the last
 * answer (useOnline). The judging is lib/storageInventory.ts.
 */

export const loadInventory = () => api.storageInventory()

export interface InventoryState {
  data: Inventory | null
  error: string
  reload: () => void
  now: number
}

const Part = ({ title, children, testId }: { title: string; children: ReactNode; testId: string }) => (
  <section className="mb-7" data-testid={testId}>
    <h2 className="t-title2 mb-3">{title}</h2>
    {children}
  </section>
)

const Calm = ({ children, testId }: { children: ReactNode; testId?: string }) => (
  <p className="card px-3 py-3 text-sm text-muted" data-testid={testId}>
    {children}
  </p>
)

const Dot = ({ level, label }: { level: Level | 'unknown'; label?: string }) => (
  <span
    className={cx('h-2 w-2 rounded-full shrink-0', statusDot[level])}
    aria-label={label}
    aria-hidden={label ? undefined : true}
  />
)

/** A use bar: the accent while there is room, amber past 85%, the danger hue past 95%. */
const Bar = ({ pct, level, label }: { pct: number; level: Level; label: string }) => (
  <div
    className={cx('h-1.5 rounded-full overflow-hidden mt-1', useBar.track)}
    role="meter"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={pct}
    aria-label={label}
  >
    <div className={cx('h-full rounded-full', useBar[level])} style={{ width: `${Math.max(1, pct)}%` }} />
  </div>
)

/** A section whose source is off or failed says so; null when it answered. */
const SectionState = ({
  section,
  what,
  reload,
}: {
  section: StorageSection
  what: string
  reload: () => void
}) => {
  if (section.state === 'failed') return <Failed what={what} reason={section.error} onRetry={reload} />
  const off = offWords(section)
  return off ? <Calm testId={`off-${section.off}`}>{off}</Calm> : null
}

/** A machine's heading over its rows: its name, what it is, and a tag when it does not answer. */
const MachineTitle = ({ m }: { m: StorageMachine }) => (
  <SectionTitle right={m.down ? <Tag tone="warn">Not reporting</Tag> : undefined}>
    <span className="text-fg">{m.name}</span> <span className="font-normal text-faint">{machineLine(m)}</span>
  </SectionTitle>
)

const NotReporting = () => <Calm>Its node exporter does not answer, so nothing on it is known.</Calm>

const DriveRow = ({ d }: { d: PhysicalDrive }) => {
  const level = driveLevel(d)
  return (
    <li className="flex items-start gap-2.5 px-3 py-2.5 min-h-12" data-testid="drive-row">
      <span className="flex mt-[6px]">
        <Dot level={d.smart === 'asleep' ? 'unknown' : level} label={level === 'ok' ? undefined : level} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[13px] shrink-0">{d.device}</span>
          <span className="flex-1 min-w-0 truncate text-[14px] leading-5" title={d.model}>
            {driveTitle(d)}
          </span>
          {d.size ? (
            <span className="text-[12px] text-muted tabular shrink-0">{fmtBytes(d.size)}</span>
          ) : null}
        </div>
        <p
          className={cx('text-[12px] leading-snug mt-0.5', level === 'ok' ? 'text-muted' : statusText[level])}
        >
          {driveFacts(d).join(', ')}
        </p>
      </div>
    </li>
  )
}

const Drives = ({ data, reload }: { data: Inventory; reload: () => void }) => {
  const part = data.drives
  if (part.state !== 'ok') return <SectionState section={part} what="the drives" reload={reload} />
  if (!part.hosts.length) return <Calm>Prometheus knows of no machine yet.</Calm>
  return (
    <>
      {machinesByEstate(part.hosts).map((g) => (
        <div key={g.estate} data-testid={`drives-${g.estate}`}>
          <h3 className="label text-muted mb-2">{g.name}</h3>
          {g.hosts.map((h) => (
            <div key={h.name} className="mb-4">
              <MachineTitle m={h} />
              {h.down ? (
                <NotReporting />
              ) : h.drives.length ? (
                <ul className="group-list">
                  {h.drives.map((d) => (
                    <DriveRow key={d.device} d={d} />
                  ))}
                </ul>
              ) : (
                <Calm>Its exporter lists no disk.</Calm>
              )}
            </div>
          ))}
        </div>
      ))}
    </>
  )
}

const FilesystemRow = ({ f }: { f: StorageFilesystem }) => {
  const level = fsLevel(f)
  const pct = pctOf(shareOf(f))
  return (
    <li className="px-3 py-2.5 min-h-12" data-testid="fs-row">
      <div className="flex items-baseline gap-2">
        <span className="flex-1 min-w-0 truncate font-mono text-[13px] leading-5" title={f.mount}>
          {f.mount}
        </span>
        <span
          className={cx('text-[12px] tabular shrink-0', level === 'ok' ? 'text-muted' : statusText[level])}
        >
          {fsWords(f)}
        </span>
      </div>
      <Bar pct={pct} level={level} label={`${f.mount}, ${pct}% full`} />
      <div className="flex items-baseline gap-2 mt-0.5 text-[11px] text-faint">
        <span className="flex-1 min-w-0 truncate">
          {fsTypeWords(f.type)}
          {f.device && (
            <>
              , <span className="font-mono">{f.device}</span>
            </>
          )}
        </span>
        <span className="tabular shrink-0">{pct}%</span>
      </div>
    </li>
  )
}

const Filesystems = ({ data, reload }: { data: Inventory; reload: () => void }) => {
  const part = data.filesystems
  if (part.state !== 'ok') return <SectionState section={part} what="the filesystems" reload={reload} />
  if (!part.hosts.length) return <Calm>Prometheus knows of no machine yet.</Calm>
  return (
    <>
      {machinesByEstate(part.hosts).map((g) => (
        <div key={g.estate} data-testid={`filesystems-${g.estate}`}>
          <h3 className="label text-muted mb-2">{g.name}</h3>
          {g.hosts.map((h) => (
            <div key={h.name} className="mb-4">
              <MachineTitle m={h} />
              {h.down ? (
                <NotReporting />
              ) : (
                <ul className="group-list">
                  {h.filesystems.map((f) => (
                    <FilesystemRow key={f.mount} f={f} />
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      ))}
    </>
  )
}

const ShareRow = ({ s }: { s: StorageShare }) => (
  <li className="flex items-start gap-2.5 px-3 py-2.5 min-h-12" data-testid="share-row">
    <span className="shrink-0 mt-0.5">
      <Tag>{s.protocol.toUpperCase()}</Tag>
    </span>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-2">
        <span
          className={cx(
            'flex-1 min-w-0 truncate leading-5',
            s.name.startsWith('/') ? 'font-mono text-[13px]' : 'text-[15px] font-medium',
          )}
          title={s.name}
        >
          {s.name}
        </span>
        {s.clients !== undefined && (
          <span className="text-[12px] text-muted tabular shrink-0">{clientsWords(s.clients)}</span>
        )}
      </div>
      <p className="text-[12px] text-muted leading-snug mt-0.5 break-words">{shareFacts(s).join(', ')}</p>
    </div>
  </li>
)

const Shares = ({ data, reload }: { data: Inventory; reload: () => void }) => {
  const part = data.shares
  if (part.state !== 'ok') return <SectionState section={part} what="the shares" reload={reload} />
  if (!part.hosts.length)
    return (
      <Calm testId="shares-none">
        No share has reported yet: the NAS's share collector has not written its metrics.
      </Calm>
    )
  return (
    <>
      {part.hosts.map((h) => (
        <div key={h.name} className="mb-4" data-testid="share-host">
          <SectionTitle>{h.name}</SectionTitle>
          <ul className="group-list">
            {sortShares(h.shares).map((s) => (
              <ShareRow key={`${s.protocol}/${s.name}`} s={s} />
            ))}
            <li className="px-3 py-2.5 min-h-11 text-[13px] leading-5" data-testid="nfs-clients">
              {h.nfs_clients.length ? (
                <>
                  <span className="text-muted">Connected over NFS now: </span>
                  {h.nfs_clients.join(', ')}
                </>
              ) : (
                <span className="text-muted">Nobody is connected over NFS now.</span>
              )}
            </li>
          </ul>
        </div>
      ))}
    </>
  )
}

const VolumeRow = ({ v }: { v: StorageVolume }) => {
  const level = volumeLevel(v)
  const pct = volumePct(v)
  const name = v.claim ?? v.pv ?? ''
  return (
    <li className="flex items-start gap-2.5 px-3 py-2.5 min-h-12" data-testid="volume-row">
      <span className="flex mt-[6px]">
        <Dot level={level} label={level === 'ok' ? undefined : level} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="flex-1 min-w-0 truncate text-[15px] leading-5 font-medium" title={name}>
            {name}
          </span>
          <span
            className={cx('text-[12px] tabular shrink-0', level === 'ok' ? 'text-muted' : statusText[level])}
          >
            {volumeSize(v)}
          </span>
        </div>
        <p className="text-[12px] text-muted leading-snug mt-0.5 break-words">{volumeLine(v)}</p>
        {pct !== undefined && <Bar pct={pct} level={level} label={`${name}, ${pct}% full`} />}
        {v.source && (
          <p className="font-mono text-[11px] text-faint truncate mt-0.5" title={v.source}>
            {v.source}
          </p>
        )}
        <p
          className={cx('text-[12px] leading-snug mt-1', v.backup === 'covered' ? 'text-fg-2' : 'text-faint')}
        >
          {backupWords(v)}
        </p>
      </div>
      {v.claim && v.phase !== 'Bound' && (
        <span className="shrink-0">
          <Tag tone="warn">{v.phase}</Tag>
        </span>
      )}
    </li>
  )
}

const Volumes = ({ data, reload }: { data: Inventory; reload: () => void }) => {
  const part = data.volumes
  if (part.state !== 'ok') return <SectionState section={part} what="the cluster's volumes" reload={reload} />
  if (!part.volumes.length) return <Calm>The cluster has no persistent volume.</Calm>
  return (
    <>
      {volumesByWhere(part.volumes).map((g) => (
        <div key={g.where} className="mb-4" data-testid={`volumes-${g.where}`}>
          <SectionTitle>{g.name}</SectionTitle>
          <ul className="group-list">
            {g.volumes.map((v) => (
              <VolumeRow key={`${v.namespace ?? ''}/${v.claim ?? v.pv}`} v={v} />
            ))}
          </ul>
        </div>
      ))}
      {part.classes.length > 0 && (
        <div className="mb-4" data-testid="storage-classes">
          <SectionTitle>Storage classes</SectionTitle>
          <ul className="group-list">
            {part.classes.map((c) => (
              <li key={c.name} className="flex items-center gap-2 px-3 py-2 min-h-11">
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] leading-5 truncate">{c.name}</p>
                  <p className="font-mono text-[11px] text-faint truncate">{c.provisioner}</p>
                </div>
                {c.default && <Tag>Default</Tag>}
                {c.reclaim && <span className="text-[12px] text-muted shrink-0">{c.reclaim}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

const Cloud = ({ data, reload }: { data: Inventory; reload: () => void }) => {
  const part = data.cloud
  if (part.state !== 'ok') return <SectionState section={part} what="the cloud stores" reload={reload} />
  if (!part.stores.length) return <Calm>No cloud volume, Storage Box or bucket reports yet.</Calm>
  return (
    <ul className="group-list" data-testid="cloud-stores">
      {part.stores.map((h) => (
        <HostRow key={h.name} host={h} />
      ))}
    </ul>
  )
}

const Backups = ({ data, reload, now }: { data: Inventory; reload: () => void; now: number }) => {
  const part = data.backups
  if (part.state !== 'ok') return <SectionState section={part} what="the backups" reload={reload} />
  if (!part.backups.length) return <Calm>No backup reports yet.</Calm>
  return (
    <ul className="group-list" data-testid="backups">
      {part.backups.map((b) => (
        <BackupRow key={`${b.kind}/${b.name}`} backup={b} now={now} />
      ))}
    </ul>
  )
}

const Loading = () => (
  <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading" data-testid="inventory-loading">
    <Skeleton className="h-8 w-32" />
    <Skeleton className="h-40" />
    <Skeleton className="h-32" />
    <Skeleton className="h-8 w-40" />
    <Skeleton className="h-48" />
  </div>
)

/** The Storage page's body: each section in turn, each with its own state. */
export const StorageInventory = ({ data, error, reload, now }: InventoryState) => {
  if (!data)
    return error ? <Failed what="the storage inventory" reason={error} onRetry={reload} /> : <Loading />
  return (
    <>
      <Part title="Drives" testId="inventory-drives">
        <Drives data={data} reload={reload} />
      </Part>
      <Part title="Filesystems" testId="inventory-filesystems">
        <Filesystems data={data} reload={reload} />
      </Part>
      <Part title="Shares" testId="inventory-shares">
        <Shares data={data} reload={reload} />
      </Part>
      <Part title="Kubernetes volumes" testId="inventory-volumes">
        <Volumes data={data} reload={reload} />
      </Part>
      <Part title="Cloud" testId="inventory-cloud">
        <Cloud data={data} reload={reload} />
      </Part>
      <Part title="Backups" testId="inventory-backups">
        <Backups data={data} reload={reload} now={now} />
      </Part>
    </>
  )
}
